import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import {
    Users,
    FileText,
    Shield,
    Clock,
    Plus,
    Search,
    Filter,
    MoreVertical,
    Edit,
    Trash2,
    UserPlus,
    Key,
    CheckCircle,
    XCircle,
    AlertCircle,
    Activity,
    BarChart3,
    TrendingUp,
    Globe,
    Settings,
    Lock,
    Mail,
    Building,
    UserCheck,
    UserX
} from 'lucide-react';
import api from '../../services/api';

export default function AdminDashboard() {
    const { user } = useSelector((state) => state.auth);
    const [activeTab, setActiveTab] = useState('overview');
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [newUserForm, setNewUserForm] = useState({
        email: '',
        full_name: '',
        role: 'agent',
        company_name: '',
    });
    const [createError, setCreateError] = useState('');
    const [createSuccess, setCreateSuccess] = useState('');
    const [resetPasswordUser, setResetPasswordUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState('');

    // Fetch stats
    const { data: stats, isLoading: statsLoading } = useQuery('admin-stats', () =>
        api.get('/dashboard/stats').then((res) => res.data)
    );

    // Fetch all users
    const {
        data: users,
        isLoading: usersLoading,
        refetch: refetchUsers,
    } = useQuery('admin-users', () => api.get('/users').then((res) => res.data));

    // Fetch audit logs
    const { data: logs, isLoading: logsLoading } = useQuery('admin-logs', () =>
        api.get('/admin/logs').then((res) => res.data)
    );

    // Fetch rate cards
    const { data: rateCards, isLoading: ratesLoading } = useQuery('admin-rates', () =>
        api.get('/underwriters/rates').then((res) => res.data)
    );

    const isUserActive = (status) => {
        return status === true || status === 1 || status === '1' || status === 'true';
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreateError('');
        setCreateSuccess('');
        try {
            const response = await api.post('/admin/users', newUserForm);
            if (response.data.email_sent === false) {
                setCreateError(response.data.message);
            } else {
                setCreateSuccess(response.data.message || 'User created successfully!');
                setTimeout(() => setShowCreateUser(false), 1500);
            }
            setNewUserForm({ email: '', full_name: '', role: 'agent', company_name: '' });
            refetchUsers();
        } catch (err) {
            setCreateError(err.response?.data?.error || 'Failed to create user');
        }
    };

    const handleToggleUserStatus = async (userId, currentStatus) => {
        try {
            const newStatus = !isUserActive(currentStatus);
            await api.put(`/admin/users/${userId}/status`, { is_active: newStatus });
            refetchUsers();
        } catch (err) {
            console.error('Failed to update user status:', err);
        }
    };

    const handleResetPassword = async (userId) => {
        setResetError('');
        setResetSuccess('');
        try {
            await api.put(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
            setResetSuccess('Password reset successfully!');
            setTimeout(() => {
                setResetPasswordUser(null);
                setNewPassword('');
                setResetSuccess('');
            }, 2000);
        } catch (err) {
            setResetError(err.response?.data?.error || 'Failed to reset password');
        }
    };

    const tabs = [
        { key: 'overview', label: 'Overview', icon: BarChart3 },
        { key: 'users', label: 'User Management', icon: Users },
        { key: 'logs', label: 'Audit Logs', icon: Clock },
        { key: 'rates', label: 'Rate Cards', icon: TrendingUp },
    ];

    const filteredUsers = users?.filter(u =>
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.role?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (statsLoading) return <SkeletonLoader />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* ========== HERO BANNER ========== */}
                <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-2xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Shield className="w-5 h-5 text-green-300" />
                                <span className="text-green-200 text-xs uppercase tracking-widest font-semibold">Admin Panel</span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-extrabold">
                                Welcome back, {user?.full_name || 'Admin'} 👋
                            </h1>
                            <p className="text-green-100/80 mt-2 max-w-2xl">
                                Manage users, monitor system activity, and oversee rate cards from one centralized dashboard.
                            </p>
                        </div>

                    </div>
                </div>

                {/* ========== STAT CARDS ========== */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        icon={Users}
                        label="Total Users"
                        value={users?.length || 0}
                        gradient="from-[#2d5232] to-[#3a663f]"
                        subtitle="Active accounts"
                    />
                    <StatCard
                        icon={FileText}
                        label="Total Quotes"
                        value={stats?.totalQuotes || 0}
                        gradient="from-[#3a663f] to-[#4d8c54]"
                        subtitle="All time"
                    />
                    <StatCard
                        icon={Shield}
                        label="Active Policies"
                        value={stats?.policies || 0}
                        gradient="from-[#4d8c54] to-[#5fa06a]"
                        subtitle="In force"
                    />
                    <StatCard
                        icon={Activity}
                        label="Audit Entries"
                        value={logs?.length || 0}
                        gradient="from-[#5fa06a] to-[#7ab885]"
                        subtitle="System logs"
                    />
                </div>

                {/* ========== TAB NAVIGATION ========== */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-1 flex flex-wrap gap-1">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === tab.key
                                    ? 'bg-[#3a663f] text-white shadow-lg'
                                    : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* ========== TAB CONTENT ========== */}
                <div className="animate-fadeIn">
                    {/* OVERVIEW TAB */}
                    {activeTab === 'overview' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <GlassCard
                                title="Recent Users"
                                icon={Users}
                                count={users?.length}
                                action="View All"
                            >
                                {usersLoading ? (
                                    <LoadingRows />
                                ) : (
                                    users?.slice(0, 5).map((u) => <UserRow key={u.id} user={u} />)
                                )}
                            </GlassCard>

                            <GlassCard
                                title="Recent Activity"
                                icon={Activity}
                                count={logs?.length}
                                action="View All"
                            >
                                {logsLoading ? (
                                    <LoadingRows />
                                ) : (
                                    logs?.slice(0, 5).map((log, i) => (
                                        <ActivityItem key={i} log={log} />
                                    ))
                                )}
                            </GlassCard>
                        </div>
                    )}

                    {/* USER MANAGEMENT TAB */}
                    {activeTab === 'users' && (
                        <div className="space-y-6">
                            {/* Header with Search and Create */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                        <Users className="w-5 h-5 text-[#3a663f]" />
                                        User Management
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-slate-400">
                                        Managing {users?.length || 0} users in the system
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all w-48 sm:w-64"
                                        />
                                    </div>
                                    <button
                                        onClick={() => setShowCreateUser(!showCreateUser)}
                                        className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white px-4 py-2 rounded-lg font-medium transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                    >
                                        <UserPlus className="w-4 h-4" />
                                        Create User
                                    </button>
                                </div>
                            </div>

                            {/* Create User Form */}
                            {showCreateUser && (
                                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-6 animate-fadeIn">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                                            <UserPlus className="w-5 h-5 text-[#3a663f]" />
                                            Create New User
                                        </h4>
                                        <button
                                            onClick={() => setShowCreateUser(false)}
                                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    {createError && (
                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-3 rounded-lg text-sm mb-4 flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            {createError}
                                        </div>
                                    )}
                                    {createSuccess && (
                                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 p-3 rounded-lg text-sm mb-4 flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4" />
                                            {createSuccess}
                                        </div>
                                    )}

                                    <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Full Name</label>
                                            <input
                                                type="text"
                                                placeholder="John Doe"
                                                value={newUserForm.full_name}
                                                onChange={(e) => setNewUserForm({ ...newUserForm, full_name: e.target.value })}
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Email</label>
                                            <input
                                                type="email"
                                                placeholder="john@example.com"
                                                value={newUserForm.email}
                                                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Role</label>
                                            <select
                                                value={newUserForm.role}
                                                onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                            >
                                                <option value="agent">Agent</option>
                                                <option value="broker">Broker</option>
                                                <option value="direct_client">Direct Client</option>
                                                <option value="underwriter">Underwriter</option>
                                                <option value="admin">Admin</option>
                                            </select>
                                        </div>
                                        <div className="md:col-span-2 space-y-1">
                                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Company Name (Optional)</label>
                                            <input
                                                type="text"
                                                placeholder="Company Name"
                                                value={newUserForm.company_name}
                                                onChange={(e) => setNewUserForm({ ...newUserForm, company_name: e.target.value })}
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                            />
                                        </div>
                                        <div className="md:col-span-2 flex gap-3 pt-2">
                                            <button
                                                type="button"
                                                onClick={() => setShowCreateUser(false)}
                                                className="flex-1 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="flex-1 py-2.5 bg-[#3a663f] text-white rounded-lg font-semibold hover:bg-[#2d5231] transition-colors shadow-lg"
                                            >
                                                Create User
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* Users Table */}
                            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-slate-700/50">
                                            <tr>
                                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">User</th>
                                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Email</th>
                                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Role</th>
                                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                                                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                            {filteredUsers?.map((u) => {
                                                const active = isUserActive(u.is_active);
                                                return (
                                                    <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors group">
                                                        <td className="py-3 px-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                                                    {u.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                                                </div>
                                                                <span className="font-medium text-gray-800 dark:text-white">{u.full_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 text-gray-500 dark:text-slate-400">{u.email}</td>
                                                        <td className="py-3 px-4">
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-[#3a663f]/10 dark:bg-[#3a663f]/20 text-[#3a663f] dark:text-green-400 text-xs font-medium rounded-full capitalize">
                                                                {u.role?.replace('_', ' ')}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${active
                                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                                                }`}>
                                                                {active ? (
                                                                    <>
                                                                        <UserCheck className="w-3 h-3" />
                                                                        Active
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <UserX className="w-3 h-3" />
                                                                        Inactive
                                                                    </>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <button
                                                                    onClick={() => handleToggleUserStatus(u.id, u.is_active)}
                                                                    className={`text-xs px-3 py-1 rounded-lg font-medium transition-all ${active
                                                                        ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                                        : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                                        }`}
                                                                >
                                                                    {active ? 'Deactivate' : 'Activate'}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setResetPasswordUser(u.id);
                                                                        setNewPassword('');
                                                                        setResetError('');
                                                                        setResetSuccess('');
                                                                    }}
                                                                    className="text-xs px-3 py-1 rounded-lg font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all flex items-center gap-1"
                                                                >
                                                                    <Key className="w-3 h-3" />
                                                                    Reset Pwd
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {filteredUsers?.length === 0 && (
                                    <div className="text-center py-12">
                                        <div className="flex flex-col items-center">
                                            <Search className="w-12 h-12 text-gray-300 dark:text-slate-600 mb-3" />
                                            <p className="text-gray-500 dark:text-slate-400">No users found matching your search</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* AUDIT LOGS TAB */}
                    {activeTab === 'logs' && (
                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-[#3a663f]" />
                                        System Audit Logs
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                                        {logs?.length || 0} entries recorded
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-1">
                                        <Filter className="w-3 h-3" />
                                        Filter
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-slate-700/50">
                                        <tr>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Date/Time</th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">User</th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Action</th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Entity</th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Entity ID</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                        {logs?.map((log, i) => (
                                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                                                <td className="py-3 px-4 text-gray-500 dark:text-slate-400 whitespace-nowrap text-xs">
                                                    {new Date(log.created_at).toLocaleString()}
                                                </td>
                                                <td className="py-3 px-4 text-gray-800 dark:text-white font-medium">
                                                    {log.user_name || `User #${log.user_id}`}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                                                        {log.action}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-gray-500 dark:text-slate-400 capitalize">{log.entity_type}</td>
                                                <td className="py-3 px-4 text-gray-500 dark:text-slate-400 font-mono text-xs">{log.entity_id}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* RATE CARDS TAB */}
                    {activeTab === 'rates' && (
                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-[#3a663f]" />
                                        Active Rate Cards
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                                        {rateCards?.length || 0} active rate cards
                                    </p>
                                </div>
                                <button className="px-4 py-2 bg-[#3a663f] text-white rounded-lg font-medium hover:bg-[#2d5231] transition-all shadow-lg flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Add Rate Card
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-slate-700/50">
                                        <tr>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Product</th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Currency</th>
                                            <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Base Rate</th>
                                            <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Min Premium</th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Effective</th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                        {rateCards?.map((rate) => (
                                            <tr key={rate.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                                                <td className="py-3 px-4 font-medium text-gray-800 dark:text-white">{rate.product_name}</td>
                                                <td className="py-3 px-4 text-gray-500 dark:text-slate-400">{rate.currency}</td>
                                                <td className="py-3 px-4 text-right text-gray-800 dark:text-white font-medium">{rate.base_rate}%</td>
                                                <td className="py-3 px-4 text-right text-gray-500 dark:text-slate-400">
                                                    {rate.currency === 'USD' ? '$' : 'KSh '}{Number(rate.min_premium).toLocaleString()}
                                                </td>
                                                <td className="py-3 px-4 text-gray-500 dark:text-slate-400 whitespace-nowrap text-xs">
                                                    {new Date(rate.effective_from).toLocaleDateString()}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${rate.is_active
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                        : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                                        }`}>
                                                        {rate.is_active ? (
                                                            <>
                                                                <CheckCircle className="w-3 h-3" />
                                                                Active
                                                            </>
                                                        ) : (
                                                            <>
                                                                <XCircle className="w-3 h-3" />
                                                                Inactive
                                                            </>
                                                        )}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Reset Password Modal */}
                {resetPasswordUser && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 max-w-md w-full">
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                    <Key className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">Reset Password</h3>
                                    <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                                        Enter a new password for this user. Minimum 6 characters.
                                    </p>
                                </div>

                                {resetError && (
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-3 rounded-lg text-sm flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        {resetError}
                                    </div>
                                )}
                                {resetSuccess && (
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 p-3 rounded-lg text-sm flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4" />
                                        {resetSuccess}
                                    </div>
                                )}

                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                        className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                        required
                                        minLength="6"
                                    />
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setResetPasswordUser(null)}
                                        className="flex-1 py-3 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => handleResetPassword(resetPasswordUser)}
                                        disabled={!newPassword || newPassword.length < 6}
                                        className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-lg"
                                    >
                                        Reset Password
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function StatCard({ icon: Icon, label, value, gradient, subtitle }) {
    return (
        <div className={`bg-gradient-to-br ${gradient} text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm opacity-80">{label}</p>
                    <p className="text-3xl font-extrabold mt-1">{value.toLocaleString()}</p>
                    {subtitle && <p className="text-xs opacity-60 mt-1">{subtitle}</p>}
                </div>
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Icon className="w-6 h-6" />
                </div>
            </div>
        </div>
    );
}

function GlassCard({ title, icon: Icon, count, action, children }) {
    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {Icon && <Icon className="w-5 h-5 text-[#3a663f]" />}
                    <h3 className="font-bold text-gray-800 dark:text-white">{title}</h3>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 dark:text-slate-500">{count} total</span>
                    {action && (
                        <button className="text-xs text-[#3a663f] hover:text-[#2d5231] font-medium">
                            {action} →
                        </button>
                    )}
                </div>
            </div>
            <div className="p-4 space-y-1 max-h-80 overflow-y-auto">
                {children}
            </div>
        </div>
    );
}

function UserRow({ user }) {
    return (
        <div className="flex items-center justify-between py-2.5 px-2 hover:bg-gray-50 dark:hover:bg-slate-700/30 rounded-lg transition-colors">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">{user.full_name}</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{user.email}</p>
                </div>
            </div>
            <span className="px-2 py-0.5 bg-[#3a663f]/10 dark:bg-[#3a663f]/20 text-[#3a663f] dark:text-green-400 text-xs font-medium rounded-full capitalize">
                {user.role?.replace('_', ' ')}
            </span>
        </div>
    );
}

function ActivityItem({ log }) {
    return (
        <div className="flex items-start gap-3 py-2.5 px-2 hover:bg-gray-50 dark:hover:bg-slate-700/30 rounded-lg transition-colors">
            <div className="w-2 h-2 rounded-full bg-[#3a663f] mt-2 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{log.action}</p>
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500">
                    <span>{new Date(log.created_at).toLocaleString()}</span>
                    <span>•</span>
                    <span>{log.user_name || `User #${log.user_id}`}</span>
                    {log.entity_type && (
                        <>
                            <span>•</span>
                            <span className="capitalize">{log.entity_type}</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function LoadingRows() {
    return (
        <div className="space-y-3">
            {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-gray-100 dark:bg-slate-700 rounded-lg animate-pulse" />
            ))}
        </div>
    );
}

function SkeletonLoader() {
    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8 animate-pulse">
            <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
            <div className="grid grid-cols-4 gap-5">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-32 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                ))}
            </div>
            <div className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl" />
            <div className="grid grid-cols-2 gap-6">
                <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded-xl" />
            </div>
        </div>
    );
}